import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Radio,
  RadioGroup,
  Paper,
  styled,
  CircularProgress,
  useMediaQuery,
  Theme
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { uploadFile, fetchApi, uploadBase64Logo } from '../services/api';
import SideMenu from '../components/SideMenu';
import AlertSnackbar from '../components/AlertSnackbar';


interface CompanyInfo {
  사업자등록번호유무: boolean;
  사업자등록번호: string;
  업종: string;
  업태: string;
  대표자: string;
  대표전화: string;
  대표이메일: string;
  소재지: string;
  우편번호: string;
  기본주소: string;
  상세주소: string;
  법인여부: boolean;
  거래처구분: string;
  거래처구분코드: string;
  업체번호: number;
  업체명: string;
  통신판매업신고여부: boolean;
  통신판매업미신고사유: string;
  통신판매업신고번호: string;
  관광사업자번호: string;
  납세자상태: string;
  과세유형: string;
  세금계산서수신이메일: string;
  영업보증보험가입여부: boolean;
  영업보증보험료: number;
  기획여행보증보험가입여부: boolean;
  기획여행보증보험료: number;
  업체코드 : string;
}

interface CompanyResponse {
  table: Array<{
    회사로고: string;
    서명명판경로: string;
  }>;
  table1: Array<CompanyInfo>;
  table2: Array<{
    부서명내역: string;
  }>;
  table3: Array<{
    직책명내역: string;
  }>;
  table4: Array<{
    회사소개: string;
    사업자등록증파일명: string;
    보험보험증서명: string;
    기타증명서명: string;
    사업자등록증파일경로: string;
    보험보험증서파일경로: string;
    기타증명서명파일경로: string;
    사업자등록증파일유무: boolean;
    보험보험증서파일유무: boolean;
    기타증명서명파일유무: boolean;
  }>;
  table5: Array<{
    네이버카페: string;
    네이버블로그: string;
    네이버포스트: string;
    페이스북: string;
    인스타그램: string;
    유투브: string;
    카카오채널: string;
  }>;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2),
  maxWidth: 1000,
  marginLeft: 'auto',
  marginRight: 'auto'
}));

const drawerWidth = 240;

const CompanyInfoPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyIntroduction, setCompanyIntroduction] = useState<string>('');
  const [companycode, setCompanyCode] = useState<string>('');
  const companyInfoMemo = useMemo(() => companyInfo, [companyInfo]);
  const companyLogoMemo = useMemo(() => companyLogo, [companyLogo]);
  const companyIntroductionMemo = useMemo(() => companyIntroduction, [companyIntroduction]);

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    let isSubscribed = true;

    const fetchCompanyInfo = async () => {
      const CompanyNo = localStorage.getItem('CompanyNo');
      if (!CompanyNo) {
        if (isSubscribed) {
          setError('Company ID not found');
          setLoading(false);
        }
        return;
      }

      try {
        const postData = [{ "컬럼명": '@업체번호', "값": CompanyNo }];
        const response = await fetchApi<CompanyResponse>('DBO.RCT_S_회사기본정보', postData);
        
        if (isSubscribed && response?.data?.table1?.[0]) {
          const companyData = response.data.table1[0];
          const introData = response.data.table4[0].회사소개;
          const logoData = response.data.table[0].회사로고;

          setCompanyInfo(companyData);
          setCompanyIntroduction(introData);
          setCompanyLogo(logoData);
        }
      } catch (err) {
        if (isSubscribed) {
          setError('회사 정보를 불러오는데 실패했습니다.');
        }
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }


      try {
        const token = localStorage.getItem('token');
        const CompanyNo = localStorage.getItem('CompanyNo');
        const UserNo = localStorage.getItem('UserNo');
        const procedureName = "DBO.CW_S_유저정보";
        const postData = [
          { "컬럼명": "@업체번호", "값": CompanyNo },
          { "컬럼명": "@유저번호", "값": UserNo }
        ];
  
        const response = await fetchApi<any>(procedureName, postData);
        
        if (response.data && response.data.table) {
          setCompanyCode(response.data.table[0].업체코드);
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      } 
    };

    fetchCompanyInfo();

    return () => {
      isSubscribed = false;
    };
  }, []); // 빈 의존성 배열 유지

  useEffect(() => {
    if (companyInfoMemo && companyLogoMemo) {
      // 필요한 사이드 이펙트 처리
    }
  }, [companyInfoMemo, companyLogoMemo]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChange = (field: keyof CompanyInfo) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (companyInfo) {
      setCompanyInfo({
        ...companyInfo,
        [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      });
    }
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {

    const file = event.target.files?.[0];
    if (file) {
      try {
        const uploadResponse = await uploadBase64Logo(file);

        if (!uploadResponse || uploadResponse.status !== 200) {
          throw new Error(`파일 업로드 실패: ${uploadResponse?.message || '알 수 없는 오류'}`);
        }
        
        const logoUrl = uploadResponse.data.url.replace('https://kr.object.iwinv.kr/orgin/', '');
        
        const postData = [
          { "컬럼명": "@업체번호", "값": companyInfo?.업체번호 },
          { "컬럼명": "@로고경로", "값": logoUrl },
          { "컬럼명": "@변경자", "값": localStorage.getItem('UserNo') },
        ];

        const response = await fetchApi<any>("DBO.RCT_T_회사정보_로고", postData);
        if (response.status === 200) {
          setCompanyLogo(uploadResponse.data.url);
          setSnackbar({ open: true, message: '로고가 성공적으로 업데이트되었습니다.', severity: 'success' });
        }
      } catch (err) {
        setSnackbar({ open: true, message: '로고 업데이트에 실패했습니다.', severity: 'error' });
      }
    }
  };

  const handleIntroductionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyIntroduction(event.target.value);
  };

  const handleSave = async () => {
    if (!companyInfo) return;

    const CompanyNo = localStorage.getItem('CompanyNo');
    if (!CompanyNo) {
      setError('Company ID not found');
      return;
    }

    try {
      setLoading(true);
      const postData = [
        { "컬럼명": "@업체번호", "값": CompanyNo, "리스트값": null },
        { "컬럼명": "@사업자등록번호유무", "값": companyInfo.사업자등록번호유무, "리스트값": null },
        { "컬럼명": "@사업자등록번호", "값": companyInfo.사업자등록번호, "리스트값": null },
        { "컬럼명": "@업종", "값": companyInfo.업종, "리스트값": null },
        { "컬럼명": "@업태", "값": companyInfo.업태, "리스트값": null },
        { "컬럼명": "@대표자", "값": companyInfo.대표자, "리스트값": null },
        { "컬럼명": "@대표전화", "값": companyInfo.대표전화, "리스트값": null },
        { "컬럼명": "@대표이메일", "값": companyInfo.대표이메일, "리스트값": null },
        { "컬럼명": "@소재지", "값": "325001" }, // 기본값 설정          "리스트값": null        },
        { "컬럼명": "@우편번호", "값": companyInfo.우편번호, "리스트값": null },
        { "컬럼명": "@기본주소", "값": companyInfo.기본주소, "리스트값": null },
        { "컬럼명": "@상세주소", "값": companyInfo.상세주소, "리스트값": null },
        { "컬럼명": "@법인여부", "값": companyInfo.법인여부, "리스트값": null },
        { "컬럼명": "@거래처구분코드", "값": companyInfo.거래처구분 || "062001", "리스트값": null },
        { "컬럼명": "@업체명", "값": companyInfo.업체명, "리스트값": null },
        { "컬럼명": "@통신판매업신고여부", "값": companyInfo.통신판매업신고여부, "리스트값": null },
        { "컬럼명": "@통신판매업미신고사유", "값": "251001" }, // 기본값 설정          "리스트값": null        },
        { "컬럼명": "@통신판매업신고번호", "값": companyInfo.통신판매업신고번호, "리스트값": null },
        { "컬럼명": "@관광사업자번호", "값": companyInfo.관광사업자번호, "리스트값": null },
        { "컬럼명": "@납세자상태", "값": companyInfo.납세자상태, "리스트값": null },
        { "컬럼명": "@과세유형", "값": companyInfo.과세유형, "리스트값": null },
        { "컬럼명": "@변경자", "값": localStorage.getItem('UserId') || "", "리스트값": null },
        { "컬럼명": "@세금계산서수신이메일", "값": companyInfo.세금계산서수신이메일, "리스트값": null },
        { "컬럼명": "@영업보증보험가입여부", "값": companyInfo.영업보증보험가입여부, "리스트값": null },
        { "컬럼명": "@영업보증보험료", "값": companyInfo.영업보증보험료 || 0, "리스트값": null },
        { "컬럼명": "@기획여행보증보험가입여부", "값": companyInfo.기획여행보증보험가입여부, "리스트값": null },
        { "컬럼명": "@기획여행보증보험료", "값": companyInfo.기획여행보증보험료 || 0, "리스트값": null },
        { "컬럼명": "@회사소개", "값": companyIntroduction, "리스트값": null }
      ];

      await fetchApi('DBO.RCT_T_회사정보_기본정보', postData);
      setSnackbar({
        open: true,
        message: '저장되었습니다.',
        severity: 'success'
      });
    } catch (err) {
      setError('저장 중 오류가 발생했습니다.');
      console.error('Save Error:', err);
      setSnackbar({
        open: true,
        message: '저장 중 오류가 발생했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(companycode);
    setSnackbar({
      open: true,
      message: '업체코드를 클립보드에 복사하였습니다.',
      severity: 'success'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <SideMenu
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` }
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex' }}>
        <SideMenu
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` }
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography color="error">{error}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <SideMenu
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` }
        }}
      >
          <Paper
            elevation={0}
            sx={{
              ...(isMobile ? { p: 1.5, mt: 6 } : { py: 2.1, px: 1.6, m: 1.5, mt: 0 }),
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
              position: 'sticky',
              top: 0,
              zIndex: 9999,
              height: '62px',
            }}
          >
          <Box sx={{ mb: 0, display: 'flex',alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
              <BusinessIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                {isMobile ? '회사' : '회사정보'}
              </Typography>
            </Box>
          
            <Button variant="outlined" size="small" color="primary" onClick={handleCopy} sx={{ height: '36px' , fontweight: '700'}}>
              업체코드 : {companycode}
            </Button>
          </Box>
        </Paper>
        <Paper
          elevation={0}
          sx={{
            ...(isMobile ? { p: 3, mt: 6 } : { p: 3, m: 1.5, mt: 0 , maxWidth: 1200}),
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>회사 로고</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {companyLogo && <img src={companyLogo} alt="Company Logo" style={{ maxWidth: 300, maxHeight: 80 }} />}
              <Button variant="contained" component="label">
                Upload Logo
                <input type="file" hidden onChange={handleLogoChange} />
              </Button>
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>회사 소개</Typography>
            <TextField
              label="회사소개"
              multiline
              rows={4}
              value={companyIntroduction}
              onChange={handleIntroductionChange}
              fullWidth
              margin="normal"
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={companyInfo?.사업자등록번호유무 ?? false}
                  onChange={handleChange('사업자등록번호유무')}
                />
              }
              label="사업자 등록번호가 있습니다"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography component="label" sx={{ color: '#666', mb: 1, display: 'block' }}>
              <span style={{ color: 'red' }}>*</span>사업자등록번호
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={companyInfo?.사업자등록번호 || ''}
                onChange={handleChange('사업자등록번호')}
                size="small"
                sx={{ flex: 1 }}
              />
              {/* <Button
                variant="contained"
                sx={{ bgcolor: '#333', '&:hover': { bgcolor: '#444' } }}
              >
                사업자 등록번호 조회
              </Button> */}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>납세자 상태</Typography>
              <TextField
                value={companyInfo?.납세자상태 || ''}
                onChange={handleChange('납세자상태')}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>과세유형</Typography>
              <TextField
                value={companyInfo?.과세유형 || ''}
                onChange={handleChange('과세유형')}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>
                <span style={{ color: 'red' }}>*</span>업체명
              </Typography>
              <TextField
                value={companyInfo?.업체명 || ''}
                onChange={handleChange('업체명')}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>
                <span style={{ color: 'red' }}>*</span>대표자명
              </Typography>
              <TextField
                value={companyInfo?.대표자 || ''}
                onChange={handleChange('대표자')}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>
                <span style={{ color: 'red' }}>*</span>업태
              </Typography>
              <TextField
                value={companyInfo?.업태 || ''}
                onChange={handleChange('업태')}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>
                <span style={{ color: 'red' }}>*</span>업종
              </Typography>
              <TextField
                value={companyInfo?.업종 || ''}
                onChange={handleChange('업종')}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>
                <span style={{ color: 'red' }}>*</span>대표전화
              </Typography>
              <TextField
                value={companyInfo?.대표전화 || ''}
                onChange={handleChange('대표전화')}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>
                <span style={{ color: 'red' }}>*</span>이메일
              </Typography>
              <TextField
                value={companyInfo?.대표이메일 || ''}
                onChange={handleChange('대표이메일')}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>
                <span style={{ color: 'red' }}>*</span>기업체구분
              </Typography>
              <TextField
                value={companyInfo?.거래처구분 || ''}
                onChange={handleChange('거래처구분')}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#666', mb: 1 }}>관할 사업자번호</Typography>
              <TextField
                value={companyInfo?.관광사업자번호 || ''}
                onChange={handleChange('관광사업자번호')}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>세금계산서 수신 이메일</Typography>
            <TextField
              value={companyInfo?.세금계산서수신이메일 || ''}
              onChange={handleChange('세금계산서수신이메일')}
              fullWidth
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={companyInfo?.영업보증보험가입여부 ?? false}
                    onChange={handleChange('영업보증보험가입여부')}
                  />
                }
                label="영업보증보험 가입금액"
              />
              <TextField
                value={companyInfo?.영업보증보험료 || ''}
                onChange={handleChange('영업보증보험료')}
                fullWidth
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={companyInfo?.기획여행보증보험가입여부 ?? false}
                    onChange={handleChange('기획여행보증보험가입여부')}
                  />
                }
                label="기획여행보증보험 가입금액"
              />
              <TextField
                value={companyInfo?.기획여행보증보험료 || ''}
                onChange={handleChange('기획여행보증보험료')}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={companyInfo?.통신판매업신고여부 ?? false}
                  onChange={handleChange('통신판매업신고여부')}
                />
              }
              label="통신판매업 신고"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ minWidth: 120 }}>통신판매업 신고번호</Typography>
              <TextField
                value={companyInfo?.통신판매업신고번호 || ''}
                onChange={handleChange('통신판매업신고번호')}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={companyInfo?.법인여부 ?? false}
                  onChange={handleChange('법인여부')}
                />
              }
              label="법인여부"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ mb: 2 }}>• 주소를 검색하여 사업장 주소를 등록해 주세요.</Typography>
            <RadioGroup
              row
              value="국내"
              onChange={() => { }}
            >
              <FormControlLabel value="국내" control={<Radio />} label="국내" />
              <FormControlLabel value="해외" control={<Radio />} label="해외" />
            </RadioGroup>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>기본 주소</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={companyInfo?.기본주소 || ''}
                onChange={handleChange('기본주소')}
                fullWidth
                size="small"
              />
              {/* <Button
                variant="contained"
                sx={{ bgcolor: '#333', '&:hover': { bgcolor: '#444' } }}
              >
                주소 검색
              </Button> */}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>상세 주소</Typography>
            <TextField
              value={companyInfo?.상세주소 || ''}
              onChange={handleChange('상세주소')}
              fullWidth
              size="small"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>우편번호</Typography>
            <TextField
              value={companyInfo?.우편번호 || ''}
              onChange={handleChange('우편번호')}
              fullWidth
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                width: '100%',
                bgcolor: '#00bcd4',
                '&:hover': { bgcolor: '#00acc1' },
                py: 1.5
              }}
            >
              기본설정 저장
            </Button>
          </Box>
        </Paper>
      </Box>
      <AlertSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        
      />
    </Box>
  );
};

export default CompanyInfoPage;
